import { useEffect, useState } from "react";
import axios from "axios";
import Select from "react-select";

const API_URL = "https://general.apivcm.shop/api/countries/all";

/**
 * CountryDropdown Component
 *
 * A reusable dropdown component for selecting countries.
 * Uses react-select for full styling control.
 *
 * @component
 * @param {Object} props - Component props
 * @param {string} props.value - Selected country name
 * @param {Function} props.onChange - Callback when country is selected (receives country name)
 * @param {string} [props.placeholder="Select Country"] - Placeholder text
 * @param {boolean} [props.isRequired=false] - Whether field is required
 * @param {boolean} [props.disabled=false] - Whether dropdown is disabled
 * @param {string} [props.className=""] - Additional CSS classes
 * @param {string} [props.width="100%"] - Width of the dropdown
 * @param {Object} [props.customStyles={}] - Custom react-select styles to merge/override defaults
 */
export default function CountryDropdown({
  value = "",
  onChange = () => {},
  placeholder = "Select Country",
  isRequired = false,
  disabled = false,
  className = "",
  width = "100%",
  customStyles = {},
}) {
  const [countries, setCountries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchCountries = async () => {
      try {
        setLoading(true);
        setError(null);

        const response = await axios.get(API_URL);

        if (response.data.success && response.data.data) {
          const sortedCountries = [...response.data.data].sort((a, b) =>
            a.country.localeCompare(b.country),
          );
          setCountries(sortedCountries);
        } else {
          setError("Failed to load countries");
        }
      } catch (err) {
        console.error("Error fetching countries:", err);
        setError("Unable to load countries. Please try again.");
      } finally {
        setLoading(false);
      }
    };

    fetchCountries();
  }, []);

  // Map API data to react-select option format
  const options = countries.map((country) => ({
    value: country.country,  // Use country NAME instead of code
    label: `${country.flag} ${country.country}`,
  }));

  // Find the currently selected option object (react-select needs the full object, not just the value)
  const selectedOption = options.find((opt) => opt.value === value) || null;

  const handleChange = (selected) => {
    onChange(selected ? selected.value : "");
  };

  // Default styles — override any part via the customStyles prop
  const defaultStyles = {
    control: (base, state) => ({
      ...base,
      height: "48px",
      fontSize: "1rem",
      borderRadius: "12px",
      backgroundColor: "#f7fafc",
      border: state.isFocused ? "2px solid #86b7fe" : "2px solid #e2e8f0",
      boxShadow: state.isFocused
        ? "0 0 0 0.25rem rgba(13,110,253,.25)"
        : "none",
      cursor: disabled ? "not-allowed" : "pointer",
      "&:hover": {
        border: "2px solid #86b7fe",
      },
    }),
    // The scrollable list container
    menuList: (base) => ({
      ...base,
      maxHeight: "220px", // How tall the list can grow before scrolling
      overflowY: "auto",
      padding: "4px 0",
      // Custom scrollbar (webkit only)
      "&::-webkit-scrollbar": { width: "6px" },
      "&::-webkit-scrollbar-track": { background: "#f1f1f1" },
      "&::-webkit-scrollbar-thumb": {
        background: "#c1c1c1",
        borderRadius: "3px",
      },
    }),
    // The white card that wraps the list
    menu: (base) => ({
      ...base,
      borderRadius: "12px",
      border: "1px solid #e2e8f0",
      boxShadow: "0 4px 16px rgba(0,0,0,0.10)",
      overflow: "hidden", // Clips list to the card's rounded corners
      zIndex: 9999,
    }),
    // Individual rows
    option: (base, state) => ({
      ...base,
      backgroundColor: state.isSelected
        ? "#0d6efd"
        : state.isFocused
          ? "#e8f0fe"
          : "white",
      color: state.isSelected ? "white" : "#212529",
      padding: "10px 16px",
      cursor: "pointer",
      fontSize: "0.95rem",
    }),
    placeholder: (base) => ({
      ...base,
      color: "#6c757d",
    }),
    singleValue: (base) => ({
      ...base,
      color: "#212529",
    }),
  };

  // Deep-merge customStyles on top of defaults per react-select key
  const mergedStyles = Object.keys(defaultStyles).reduce(
    (acc, key) => {
      acc[key] = customStyles[key]
        ? (base, state) =>
            customStyles[key](defaultStyles[key](base, state), state)
        : defaultStyles[key];
      return acc;
    },
    { ...customStyles },
  );

  return (
    <div style={{ width }}>
      <Select
        value={selectedOption}
        onChange={handleChange}
        options={options}
        placeholder={
          loading
            ? "Loading countries..."
            : error
              ? "Error loading countries"
              : placeholder
        }
        isDisabled={disabled || loading}
        isLoading={loading}
        isSearchable
        required={isRequired}
        className={className}
        classNamePrefix="country-select"
        styles={mergedStyles}
      />

      {error && <small className="text-danger d-block mt-2">{error}</small>}
    </div>
  );
}